import {useState, useCallback, useEffect, useRef} from 'react';
import {Workday} from '../api/WorkDayRecordApi';
import {ErrorDetail} from '../api/types';
import {fetchWorkDayRecordsRange} from "../api/MeApi";
import {DateUtils} from "../util/DateUtils";

export const VISIBLE_DAYS = 3;

function shiftDays(date: Date, delta: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    return d;
}

function getToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

export interface UseWorkDayRecordsReturn {
    /** Records sorted oldest → newest, length ≤ VISIBLE_DAYS. */
    workdays: Workday[];
    /** The date string (YYYY-MM-DD) of the rightmost (newest) column. */
    windowEnd: string;
    loading: boolean;
    error: ErrorDetail | null;
    /** Navigate one day toward the past. */
    goOlder: () => void;
    /** Navigate one day toward the present. */
    goNewer: () => void;
    /** Whether the window can still move toward the present. */
    canGoNewer: boolean;
    refetch: () => void;
}

export function useWorkdayRecords(): UseWorkDayRecordsReturn {

    const [endOffset, setEndOffset] = useState(0);
    const [workdays, setWorkdays] = useState<Workday[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<ErrorDetail | null>(null);
    const isMountedRef = useRef(true);
    // Initialize to midnight immediately so the first render is correct
    const todayRef = useRef<Date>(getToday());

    useEffect(() => {
        todayRef.current = getToday();
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const load = useCallback(async (offset: number) => {
        setLoading(true);
        setError(null);
        try {
            console.log('ABOUT TO GO DOWN!!!')
            const today = getToday();
            const endDate = shiftDays(today, -offset);
            const startDate = shiftDays(endDate, -(VISIBLE_DAYS - 1));
            const apiResponse = await fetchWorkDayRecordsRange(
                DateUtils.toISODate(startDate),
                DateUtils.toISODate(endDate)
            );
            if (!isMountedRef.current) return;

            if (apiResponse.data) {
                setWorkdays(apiResponse.data);
            }

        } catch (err) {
            if (!isMountedRef.current) return;
            setError(err as ErrorDetail);
            console.error('Failed to fetch work day workdays:', err);

        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(endOffset);
    }, [load, endOffset]);

    const goOlder = useCallback(() => setEndOffset(o => o + 1), []);
    const goNewer = useCallback(() => setEndOffset(o => Math.max(0, o - 1)), []);
    const endDate = shiftDays(todayRef.current, -endOffset);

    return {
        workdays: workdays,
        windowEnd: DateUtils.toISODate(endDate),
        loading,
        error,
        goOlder,
        goNewer,
        canGoNewer: endOffset > 0,
        refetch: () => load(endOffset),
    };
}